/**
 * NOTE: Stop gap for (de)activating assets that are selected under the Finder 2
 * SelectionContext. This will be removed when we migrate the rest of the app
 * to use the new SelectionContext.
 */
import { useCallback } from 'react';
import { useApolloClient } from '@frameio/graphql-client';
import {
  useActiveAssetId,
  useSetActiveAssetId,
  useSetActiveVersionId,
} from '@frameio/shared/contexts';
import { AssetParent } from '../gql/generated/AssetParent';

/**
 * @todo (FIND-469): The viewer needs several pieces of state to be cleared
 * imperatively when switching active assets. The challenge is the circular
 * dependency that would be created by importing the hooks used to clear the state
 * into this file.
 */
export function useActivateAsset() {
  const { cache } = useApolloClient();
  const setActiveAssetId = useSetActiveAssetId();
  const setActiveVersionId = useSetActiveVersionId();

  return useCallback(
    (assetId: string) => {
      const asset = cache.readFragment({
        fragment: AssetParent,
        fragmentName: 'AssetParent',
        id: cache.identify({
          __typename: 'Asset',
          id: assetId,
        }),
        returnPartialData: true,
      });

      if (!asset) return;

      if (asset.parent?.__typename === 'VersionStackAsset') {
        setActiveVersionId(asset.id);
        setActiveAssetId(asset.parent.id);
      } else {
        setActiveAssetId(asset.id);
      }
    },
    [cache, setActiveAssetId, setActiveVersionId]
  );
}

/**
 * NOTE: Stop gap for activating assets that are selected under the Finder 2
 * SelectionContext. This will be removed when we migrate the rest of the app
 * to use the new SelectionContext.
 */
export function useDeactivateAsset() {
  const { cache } = useApolloClient();
  const activeAssetId = useActiveAssetId();
  const setActiveAssetId = useSetActiveAssetId();
  const setActiveVersionId = useSetActiveVersionId();
  const activateAsset = useActivateAsset();

  return useCallback(
    (assetId: string, currentSelection: Set<string>) => {
      const asset = cache.readFragment({
        fragment: AssetParent,
        fragmentName: 'AssetParent',
        id: cache.identify({
          __typename: 'Asset',
          id: assetId,
        }),
        returnPartialData: true,
      });

      if (!asset) return;

      const targetAssetId =
        asset.parent?.__typename === 'VersionStackAsset'
          ? asset.parent.id
          : asset.id;

      if (!activeAssetId || targetAssetId !== activeAssetId) return;

      if (currentSelection.size === 1 && currentSelection.has(assetId)) {
        setActiveAssetId(undefined);
        setActiveVersionId(undefined);
      } else {
        // activate the previously selected asset
        const nextSelection = Array.from(currentSelection).filter(
          (id) => id !== assetId
        );
        activateAsset(nextSelection[nextSelection.length - 1]);
      }
    },
    [activeAssetId, activateAsset, cache, setActiveAssetId, setActiveVersionId]
  );
}
